// Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useContext, useEffect, useState } from 'react';
import { useAuthContext } from '@asgardeo/auth-react';
import { RESOURCE_SERVER_URLS } from '../configs/spl-config';
import { PermissionContext } from '../../components/Authorize';
import { extractInlineAttachmentIds, replaceInlineImageSources } from '../utils/inlineImages';

// Shared across every comment/re-render so the same inline image is fetched at most
// once, even across "Show More" pagination re-fetches of the comment list.
const dataUrlCache = new Map<string, string | null>();
const inFlightRequests = new Map<string, Promise<string | null>>();

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
   let binary = '';
   const bytes = new Uint8Array(buffer);
   const chunkSize = 0x8000;
   for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize) as unknown as number[]);
   }
   return btoa(binary);
};

// Resolves ServiceNow inline-comment images (rendered as <img src=".../<id>.iix">) into
// data: URLs by fetching each referenced attachment through the existing, already
// system-user-authenticated /attachments/{id}/download endpoint - no SN user token
// is involved anywhere in this flow.
export const useInlineAttachmentImages = (html: string, isUserAllowedtoDownloadAttachments: boolean) => {
   const { httpRequest } = useAuthContext();
   const [, setVersion] = useState(0);
   const [loading, setLoading] = useState(false);

   const ids = extractInlineAttachmentIds(html);
   const idsKey = ids.join(',');

   useEffect(() => {
      if (ids.length === 0 || !isUserAllowedtoDownloadAttachments) {
         return;
      }

      const idsToFetch = ids.filter(id => !dataUrlCache.has(id));
      if (idsToFetch.length === 0) {
         return;
      }

      let cancelled = false;
      setLoading(true);

      const fetchOne = (id: string): Promise<string | null> => {
         const existing = inFlightRequests.get(id);
         if (existing) {
            return existing;
         }
         const promise = (async (): Promise<string | null> => {
            try {
               const response = await httpRequest({
                  url: `${RESOURCE_SERVER_URLS[0]}/attachments/${id}/download`,
                  method: 'GET',
                  headers: { accept: 'application/json' },
                  responseType: 'arraybuffer',
               });
               const contentType = response.headers['content-type'] || 'application/octet-stream';
               return `data:${contentType};base64,${arrayBufferToBase64(response.data)}`;
            } catch (error) {
               console.error(`Failed to load inline attachment ${id}`, error);
               return null;
            }
         })();
         inFlightRequests.set(id, promise);
         return promise;
      };

      Promise.all(
         idsToFetch.map(async id => {
            const dataUrl = await fetchOne(id);
            dataUrlCache.set(id, dataUrl);
            inFlightRequests.delete(id);
         })
      ).then(() => {
         if (!cancelled) {
            setLoading(false);
            setVersion(v => v + 1);
         }
      });

      return () => {
         cancelled = true;
      };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [idsKey, isUserAllowedtoDownloadAttachments]);

   if (ids.length === 0) {
      return { resolvedHtml: html, loading: false };
   }

   // Only carry over ids whose fetch has actually settled - an id missing from
   // this map is still in flight and should render as a loading skeleton, not
   // be conflated with a settled `null` (failed) result.
   const resolvedMap = new Map<string, string | null>();
   ids.forEach(id => {
      if (dataUrlCache.has(id)) {
         resolvedMap.set(id, dataUrlCache.get(id)!);
      }
   });

   return {
      resolvedHtml: replaceInlineImageSources(html, resolvedMap, !isUserAllowedtoDownloadAttachments),
      loading,
   };
};
